/**
 * Campaigns — list, create/edit, and the send / schedule actions.
 *
 * A campaign is only editable while it is a draft or scheduled: once the outbox
 * holds its emails, the record has to keep matching what people received, so
 * the backend refuses the edit and this screen hides the button.
 */
define('mailingCampaignsView',[
  'module',
  'app',
  'underscore',
  'jquery',
  'marionette',
  'mailingShell',
  'entities/mailing'
], function (
  module,
  App,
  _,
  $,
  Marionette,
  Shell,
  MailingApi
) {
  'use strict';

  var STATUS_CLASS = {
    DRAFT: 'draft',
    SCHEDULED: 'scheduled',
    SENDING: 'sending',
    SENT: 'sent'
  };

  var CampaignsView = Marionette.ItemView.extend({

    className: 'mailing-page mailing-campaigns',

    template: _.template(
      '<%= nav %>' +
      '<div class="mlg-body"><div class="mlg-loading">Loading campaigns…</div></div>' +
      '<div class="mlg-modal" style="display:none"></div>'
    ),

    ui: {
      body: '.mlg-body',
      modal: '.mlg-modal'
    },

    events: {
      'click .mlg-new-campaign': 'onNew',
      'click .mlg-edit': 'onEdit',
      'click .mlg-open': 'onOpen',
      'click .mlg-send': 'onSend',
      'click .mlg-schedule': 'onSchedule',
      'click .mlg-unschedule': 'onUnschedule',
      'click .mlg-delete': 'onDelete',
      'click .mlg-modal-cancel': 'closeModal',
      'click .mlg-modal-save': 'onSave',
      'click .mlg-modal-schedule-confirm': 'onScheduleConfirm'
    },

    serializeData: function () {
      return {
        nav: Shell.navHtml('campaigns',
          '<button type="button" class="mlg-btn primary mlg-new-campaign">New campaign</button>')
      };
    },

    onShow: function () {
      this.load();
    },

    load: function () {
      var self = this;
      $.when(MailingApi.campaigns(), MailingApi.templates(), MailingApi.segments())
        .done(function (campaigns, templates, segments) {
          self.templates = templates || [];
          self.segments = segments || [];
          self.renderList(campaigns || []);
        })
        .fail(function (xhr) {
          self.ui.body.html('<div class="mlg-error">' +
            _.escape(MailingApi.errorMessage(xhr)) + '</div>');
        });
    },

    renderList: function (campaigns) {
      if (!campaigns.length) {
        this.ui.body.html(Shell.emptyHtml('No campaign yet. Create one to send a template to a segment.'));
        return;
      }
      var rows = _.map(campaigns, function (campaign) {
        var status = campaign.status || 'DRAFT';
        var editable = status === 'DRAFT' || status === 'SCHEDULED';
        var when = status === 'SCHEDULED' ?
            'Scheduled ' + Shell.formatDate(campaign.scheduledAt, true) :
            (campaign.sentAt ? 'Sent ' + Shell.formatDate(campaign.sentAt, true) :
                               'Created ' + Shell.formatDate(campaign.createDate, true));

        return '<tr data-secid="' + campaign.secId + '">' +
          '<td class="c-name">' +
          '  <a class="mlg-open" href="#mailing/campaigns/' + campaign.secId + '">' +
               _.escape(campaign.name || '(untitled)') + '</a>' +
          '  <div class="c-sub">' + _.escape(campaign.subject || '') + '</div>' +
          '</td>' +
          '<td>' + _.escape(campaign.templateName || '—') + '</td>' +
          '<td>' + _.escape(campaign.segmentName || '—') +
            (campaign.segmentCount ? ' <span class="c-count">' +
              Shell.formatNumber(campaign.segmentCount) + '</span>' : '') + '</td>' +
          '<td><span class="mlg-chip st-' + (STATUS_CLASS[status] || 'draft') + '">' +
              Shell.prettyCase(status) + '</span></td>' +
          '<td class="c-when">' + _.escape(when) + '</td>' +
          '<td class="c-actions">' +
            (editable ? '<button type="button" class="mlg-btn small mlg-edit">Edit</button>' : '') +
            (status === 'DRAFT' ? '<button type="button" class="mlg-btn small mlg-schedule">Schedule</button>' +
                                  '<button type="button" class="mlg-btn small primary mlg-send">Send now</button>' : '') +
            (status === 'SCHEDULED' ? '<button type="button" class="mlg-btn small mlg-unschedule">Cancel schedule</button>' : '') +
            (status !== 'SENDING' ? '<button type="button" class="mlg-btn small danger mlg-delete">Delete</button>' : '') +
          '</td>' +
          '</tr>';
      }).join('');

      this.ui.body.html(
        '<table class="mlg-table">' +
        '<thead><tr>' +
        '<th>Campaign</th><th>Template</th><th>Segment</th><th>Status</th><th>When</th><th></th>' +
        '</tr></thead>' +
        '<tbody>' + rows + '</tbody></table>');
    },

    //----------Create / edit------------------------------------------------//

    onNew: function () {
      this.openEditor(null);
    },

    onEdit: function (e) {
      var self = this;
      MailingApi.campaign(this.secIdOf(e)).done(function (campaign) {
        self.openEditor(campaign);
      });
    },

    openEditor: function (campaign) {
      var model = campaign || {};
      var options = function (items, selectedSecId, labelKey) {
        return '<option value=""></option>' + _.map(items, function (item) {
          return '<option value="' + item.secId + '"' +
            (item.secId === selectedSecId ? ' selected' : '') + '>' +
            _.escape(item[labelKey] || '') + '</option>';
        }).join('');
      };

      this.editingSecId = model.secId || null;
      this.ui.modal.html(
        '<div class="mlg-modal-card">' +
        '<h3>' + (model.secId ? 'Edit campaign' : 'New campaign') + '</h3>' +
        '<label>Name<input type="text" class="f-name" value="' + _.escape(model.name || '') + '"></label>' +
        '<label>Subject<input type="text" class="f-subject" value="' + _.escape(model.subject || '') + '"></label>' +
        '<div class="mlg-row">' +
        '  <label>From name<input type="text" class="f-from-name" value="' + _.escape(model.fromName || '') + '"></label>' +
        '  <label>From email<input type="text" class="f-from-email" value="' + _.escape(model.fromEmail || '') + '"></label>' +
        '</div>' +
        '<div class="mlg-hint">Leave the sender blank to use the SMTP identity from the mailing settings.</div>' +
        '<div class="mlg-row">' +
        '  <label>Template<select class="f-template">' +
             options(this.templates, model.templateSecId, 'name') + '</select></label>' +
        '  <label>Segment<select class="f-segment">' +
             options(this.segments, model.segmentSecId, 'name') + '</select></label>' +
        '</div>' +
        '<div class="mlg-modal-actions">' +
        '  <button type="button" class="mlg-btn mlg-modal-cancel">Cancel</button>' +
        '  <button type="button" class="mlg-btn primary mlg-modal-save">Save</button>' +
        '</div>' +
        '</div>').show();
    },

    onSave: function () {
      var self = this;
      var data = {
        name: this.ui.modal.find('.f-name').val(),
        subject: this.ui.modal.find('.f-subject').val(),
        fromName: this.ui.modal.find('.f-from-name').val(),
        fromEmail: this.ui.modal.find('.f-from-email').val(),
        templateSecId: this.ui.modal.find('.f-template').val(),
        segmentSecId: this.ui.modal.find('.f-segment').val()
      };
      if (!data.name || !data.subject) {
        Shell.banner(this.$el, 'Name and subject are required', 'error');
        return;
      }
      var saving = this.editingSecId ?
          MailingApi.updateCampaign(this.editingSecId, data) :
          MailingApi.createCampaign(data);

      saving.done(function () {
        self.closeModal();
        Shell.banner(self.$el, 'Campaign saved', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    closeModal: function () {
      this.editingSecId = null;
      this.ui.modal.hide().empty();
    },

    //----------Send / schedule----------------------------------------------//

    onSend: function (e) {
      var self = this;
      var secId = this.secIdOf(e);
      var name = $(e.currentTarget).closest('tr').find('.c-name a').text();
      if (!window.confirm('Queue "' + name + '" for sending now?')) { return; }

      MailingApi.sendCampaign(secId).done(function (result) {
        Shell.banner(self.$el, (result.queued || 0) +
          ' email(s) queued. They leave the outbox at the configured rate.', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onSchedule: function (e) {
      var secId = this.secIdOf(e);
      var inOneHour = new Date(Date.now() + 3600000);
      this.schedulingSecId = secId;
      this.ui.modal.html(
        '<div class="mlg-modal-card narrow">' +
        '<h3>Schedule campaign</h3>' +
        '<label>Send at<input type="datetime-local" class="f-scheduled-at" value="' +
          Shell.toLocalInputValue(inOneHour) + '"></label>' +
        '<div class="mlg-hint">The outbox is checked every minute, so sending starts within a minute of this time.</div>' +
        '<div class="mlg-modal-actions">' +
        '  <button type="button" class="mlg-btn mlg-modal-cancel">Cancel</button>' +
        '  <button type="button" class="mlg-btn primary mlg-modal-schedule-confirm">Schedule</button>' +
        '</div>' +
        '</div>').show();
    },

    onScheduleConfirm: function () {
      var self = this;
      var value = this.ui.modal.find('.f-scheduled-at').val();
      if (!value) {
        Shell.banner(this.$el, 'Pick a date and time', 'error');
        return;
      }
      var timestamp = new Date(value).getTime();
      if (!timestamp || timestamp <= Date.now()) {
        Shell.banner(this.$el, 'The scheduled time must be in the future', 'error');
        return;
      }
      MailingApi.scheduleCampaign(this.schedulingSecId, timestamp).done(function () {
        self.closeModal();
        Shell.banner(self.$el, 'Campaign scheduled', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onUnschedule: function (e) {
      var self = this;
      MailingApi.unscheduleCampaign(this.secIdOf(e)).done(function () {
        Shell.banner(self.$el, 'Schedule cancelled — the campaign is a draft again', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onDelete: function (e) {
      var self = this;
      var name = $(e.currentTarget).closest('tr').find('.c-name a').text();
      if (!window.confirm('Delete "' + name + '"? Its statistics are deleted with it.')) { return; }

      MailingApi.deleteCampaign(this.secIdOf(e)).done(function () {
        Shell.banner(self.$el, 'Campaign deleted', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onOpen: function (e) {
      e.preventDefault();
      App.navigate($(e.currentTarget).attr('href').substring(1), { trigger: true });
    },

    secIdOf: function (e) {
      return $(e.currentTarget).closest('tr').data('secid');
    }
  });

  module.exports = CampaignsView;
});

